// S2-CC20.6b.2: React hook wrapping GET /v1/llm/budgets.
//
// Single fetch on mount. The backend applies a 30s server-side cache so we
// deliberately skip any client-side caching / stale-while-revalidate —
// refresh() just re-issues the fetch. Empty `tree=[]` is valid (renders an
// empty state in the caller).
import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../utils/api';
import type { BudgetNode, ListBudgetsResponse } from '../types/llm';

export interface UseLLMBudgetsResult {
  tree: BudgetNode[];
  asOf: string | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useLLMBudgets(): UseLLMBudgetsResult {
  const [tree, setTree] = useState<BudgetNode[]>([]);
  const [asOf, setAsOf] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Single source of truth for the fetch; useEffect + refresh both call it.
  const refresh = useCallback(() => {
    setLoading(true);
    apiFetch<ListBudgetsResponse>('/v1/llm/budgets')
      .then(data => {
        setTree(data.tree);
        setAsOf(data.as_of);
        setError(null);
      })
      .catch(err => {
        setError(err instanceof Error ? err.message : 'Failed to load budgets');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { tree, asOf, loading, error, refresh };
}
